import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { BatchedChanges, ExternalAgreement, ExternalAgreementStatus } from '@/types';
import { CardSection, ExpandableCard, ExpandableCardHandle, CardRow, CardTags, CardDropdownMenu } from '@/components/Cards';
import ScrollToWrapper from '@/components/ScrollToWrapper';
import { ExternalAgreementStatusIndicator } from '@/components/StatusIndicator';
import { useDocumentType, useThreadUnreadMessageCount } from '@/utils/hooks';
import ExternalAgreementDetails from '../ExternalAgreementDetails';
import { PriceDisplay } from '@/seller/components/fees';
import { RejectedStatusReason, StatusReason } from '@/components/StatusReason';
import { DateDisplay } from '@/components/localization';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import theme from './ExternalAgreementListItem.module.scss';
import { Tooltip } from '@/components/Tooltip';
import { Dropdown, DropdownContextMenu } from '@ygtechnologies/rubus-ui';
import { ProjectRelativeLink, projectRelativeLink } from '@/components/navigation';

export interface ExternalAgreementListItemProps {
    externalAgreement: ExternalAgreement;
    onChange: (changes: BatchedChanges<ExternalAgreement>) => void;
    defaultExpanded?: boolean;
    showUnit: boolean;
}

const ExternalAgreementListItem: React.FC<ExternalAgreementListItemProps> = ({
    externalAgreement,
    defaultExpanded,
    onChange,
    showUnit,
}) => {
    const [isExpanded, setIsExpanded] = useState(!!defaultExpanded);
    const documentType = useDocumentType(externalAgreement.documentTypeId);
    const messageCount = useThreadUnreadMessageCount(externalAgreement.commentThreadId);

    return (
        <ScrollToWrapper shouldScroll={defaultExpanded}>
            <ExpandableCard
                notificationBadgeNumber={messageCount}
                wrap
                type="inside-bordered"
                expanded={isExpanded}
                expandableContent={<ExternalAgreementDetails externalAgreement={externalAgreement} onChange={onChange} />}>
                <CardRow>
                    <CardSection width="200px" caption={documentType?.name ?? ''} shrink>
                        <div>{externalAgreement.name}</div>
                    </CardSection>

                    {showUnit && (
                        <CardSection width="150px" caption={<Trans>Unit</Trans>} shrink>
                            <div>{externalAgreement.residentialUnitName}</div>
                        </CardSection>
                    )}

                    <CardSection width="270px" caption={<Trans>Uploaded by</Trans>} shrink>
                        <div>
                            {externalAgreement.createdByUser.name}
                            {externalAgreement.createDate && (
                                <>
                                    &nbsp;-&nbsp;
                                    <DateDisplay value={externalAgreement.createDate} />
                                </>
                            )}
                        </div>
                    </CardSection>

                    {externalAgreement.reviewedByUser && (
                        <CardSection width="270px" caption={<Trans>Approved by</Trans>} shrink>
                            <div>
                                {externalAgreement.reviewedByUser && externalAgreement.reviewedByUser.name}
                                {externalAgreement.reviewedDate && (
                                    <>
                                        &nbsp;-&nbsp;
                                        <DateDisplay value={externalAgreement.reviewedDate} />
                                    </>
                                )}
                            </div>
                        </CardSection>
                    )}

                    <CardSection width="170px" caption={<Trans>Price</Trans>} right shrink>
                        <div className={theme.priceContainer}>
                            <PriceDisplay
                                className="text-left"
                                value={externalAgreement}
                                redacted={externalAgreement.priceRedacted}
                            />

                            {externalAgreement.priceOutdated && (
                                <Tooltip
                                    content={
                                        <Trans>
                                            The price of this external agreement has changed, see the connected change
                                            request for updated info
                                        </Trans>
                                    }>
                                    <span className={theme.outdated}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    </CardSection>

                    <CardSection caption={<Trans>Internal status</Trans>} width="130px" right shrink>
                        <ExternalAgreementStatusIndicator value={externalAgreement.status} size="small" />

                        {externalAgreement.status === ExternalAgreementStatus.Rejected && (
                            <RejectedStatusReason
                                reason={externalAgreement.rejectReason || ''}
                                userName={externalAgreement.reviewedByUser?.name}
                            />
                        )}
                        {externalAgreement.status === ExternalAgreementStatus.Canceled && (
                            <StatusReason titleGenerator={() => ''} reason={externalAgreement.cancelReason || ''} />
                        )}
                    </CardSection>

                    <ExpandableCardHandle expanded={isExpanded} onExpandToggle={() => setIsExpanded(!isExpanded)} />
                    <CardDropdownMenu hidden={!externalAgreement.changeRequestId}>
                        <Dropdown.Item
                            as={ProjectRelativeLink}
                            to={`/residential-units/${externalAgreement.residentialUnitId}/changes/${externalAgreement.changeRequestId}`}>
                            <Trans>Go to change request</Trans>
                        </Dropdown.Item>
                    </CardDropdownMenu>
                </CardRow>
                <CardTags canCreate={false} tags={externalAgreement.tags} />
            </ExpandableCard>
        </ScrollToWrapper>
    );
};

export default ExternalAgreementListItem;
